/**
 * nearby.js - the people and the things that are already in the room.
 *
 * CONTENT ONLY. Nothing here knows what a sofa is, how a level is paced, or how
 * a box is judged.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CATALOGUE IS PEOPLE AND NOT OBJECTS
 *
 * Isaac's whole difficulty is that a screen is more interesting than the room
 * it is in. So every word this game asks for is somebody who is present, or
 * something that is only any good with somebody else in it - a hug, a picnic, a
 * song. Nothing in this file can be done alone, which is the entire content
 * argument of the game.
 *
 * At Phase 1 the framework measures whether the child produced the target
 * (component C) and whether they made a sound at all (component B). It does not
 * measure what the word MEANS. So `kind` below is not scored and never reaches
 * the engine; it exists so that a level can draw a pool of people or a pool of
 * things-done-together and the arc across the five levels is legible to the
 * next author.
 *
 * ---------------------------------------------------------------------------
 * THE WORD THIS GAME IS ABOUT IS NOT IN THIS FILE
 *
 * There is no `youtube`, no `phone`, no `tablet`, no `screen`. That is
 * deliberate and it is the most important line in the game.
 *
 * A Phase 1 target is a word Pip says aloud and the child repeats, ten or more
 * times per box, until the box takes a colour. A game whose brief is "help this
 * child stop seeking YouTube" that put "YouTube" in the pool would be drilling
 * the word it exists to quieten. So the screen is SCENERY: it is drawn, it is
 * always there, it dims as the room fills, and it is never a target, never in
 * `legalMoves`, never in an accept list and never judged. content/index.js
 * validateContent() refuses it if anybody adds it later.
 *
 * ---------------------------------------------------------------------------
 * NO OVERLAP WITH THE OTHER GAMES' CATALOGUES
 *
 * Bubble Pop also covers Phase 1 Levels 1 to 3. Section 9.3 verifies a skill as
 * generalized by seeing the same LEVEL go Green in two games; if both games
 * used the same words, Green in the second would show the child had learned the
 * words, which is the thing generalization is supposed to rule out. So nothing
 * here appears in bubble-pop/content/objects.js or in
 * door-knock/content/creatures.js.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY WORD CARRIES AN `accept` LIST
 *
 * The recogniser returns dictionary words, not what a child said: "mum" comes
 * back as "mom", "nan" as "nana". A target is therefore a SET of strings, not a
 * string. Anything a real recogniser plausibly returns for a CORRECT production
 * belongs here; anything a DIFFERENT word would also produce does not.
 *
 * And some perfectly good words are missing for the other reason. `grape` was
 * in a draft of this file and `engine/language.js` refuses it - it comes back
 * as an obscenity. `book`, `clap` and `read` were drafted and dropped for the
 * milder version of the same problem, and `dads` is not in dad's accept list
 * for it either. The screen is a screen, not a proof; think about it anyway.
 */

/**
 * @typedef {object} Nearby
 * @property {string} id            stable key; used by levels and the record
 * @property {string} word          what Pip says and the child is asked for
 * @property {string[]} accept      every transcript string that counts as
 *                                  having said it, `word` included, lowercase
 * @property {'person'|'together'} kind  not scored; see the header
 * @property {string} art           the face on the card
 * @property {string} clue          one short sentence, never containing the
 *                                  word itself - a clue that says the answer is
 *                                  a model, and a model is a different rung
 * @property {string} tint          card tint, so a field is varied without
 *                                  being noise
 * @property {string} firstLetter   drawn at the partial_verbal rung. Phase 1
 *                                  never reaches it (the phase has one rung),
 *                                  but the renderer takes it and a Phase 2
 *                                  sibling level would need it.
 */

export const NEARBY = {
  // ---- the people ---------------------------------------------------------
  mum:    { id: 'mum',    word: 'mum',    accept: ['mum', 'mummy', 'mom'],       kind: 'person',   art: '👩',  clue: 'She sings to you when the light goes out.',        tint: '#ffb3c1', firstLetter: 'M'  },
  dad:    { id: 'dad',    word: 'dad',    accept: ['dad', 'daddy'],              kind: 'person',   art: '👨',  clue: 'He lifts you right up onto his shoulders.',        tint: '#8fc7ff', firstLetter: 'D'  },
  nan:    { id: 'nan',    word: 'nan',    accept: ['nan', 'nana'],               kind: 'person',   art: '👵',  clue: 'She keeps the good biscuits in a tin.',            tint: '#e8c9a0', firstLetter: 'N'  },
  sam:    { id: 'sam',    word: 'sam',    accept: ['sam'],                       kind: 'person',   art: '🧒',  clue: 'He lives two doors along and knocks for you.',     tint: '#9fe0b0', firstLetter: 'S'  },
  ben:    { id: 'ben',    word: 'ben',    accept: ['ben'],                       kind: 'person',   art: '👦',  clue: 'He has the red helmet and waits at the gate.',     tint: '#ffcf7a', firstLetter: 'B'  },
  tom:    { id: 'tom',    word: 'tom',    accept: ['tom'],                       kind: 'person',   art: '🧑',  clue: 'He is the tall one, and he laughs first.',         tint: '#c3b2f0', firstLetter: 'T'  },
  baby:   { id: 'baby',   word: 'baby',   accept: ['baby', 'babies'],            kind: 'person',   art: '👶',  clue: 'The littlest one at home, learning words too.',    tint: '#ffd9e6', firstLetter: 'B'  },
  friend: { id: 'friend', word: 'friend', accept: ['friend', 'friends'],         kind: 'person',   art: '🤝',  clue: 'Somebody who waits for you and plays fair.',       tint: '#7fd6cd', firstLetter: 'Fr' },
  auntie: { id: 'auntie', word: 'auntie', accept: ['auntie', 'aunty', 'aunt'],   kind: 'person',   art: '👩‍🦰', clue: 'Mum\'s sister, who talks a lot and laughs louder.', tint: '#f0a8d0', firstLetter: 'Au' },
  uncle:  { id: 'uncle',  word: 'uncle',  accept: ['uncle', 'uncles'],           kind: 'person',   art: '🧔',  clue: 'Dad\'s brother, the silliest one in the house.',    tint: '#a8c48a', firstLetter: 'U'  },

  // ---- the things you cannot do on your own -------------------------------
  hug:    { id: 'hug',    word: 'hug',    accept: ['hug', 'hugs'],               kind: 'together', art: '🤗',  clue: 'Two arms right around you, and a squeeze.',        tint: '#ff9f9f', firstLetter: 'H'  },
  teddy:  { id: 'teddy',  word: 'teddy',  accept: ['teddy', 'teddies'],          kind: 'together', art: '🧸',  clue: 'Soft, two small ears, and he sleeps in your bed.', tint: '#d8ac82', firstLetter: 'T'  },
  park:   { id: 'park',   word: 'park',   accept: ['park', 'parks'],             kind: 'together', art: '🏞️',  clue: 'A big green place with room to run about.',        tint: '#8fd67a', firstLetter: 'P'  },
  bike:   { id: 'bike',   word: 'bike',   accept: ['bike', 'bikes'],             kind: 'together', art: '🚲',  clue: 'Two wheels, and you push the pedals round.',       tint: '#7fb0ff', firstLetter: 'B'  },
  swing:  { id: 'swing',  word: 'swing',  accept: ['swing', 'swings'],           kind: 'together', art: '🛝',  clue: 'You sit on it and go up, and back, and up.',       tint: '#ffd36b', firstLetter: 'Sw' },
  song:   { id: 'song',   word: 'song',   accept: ['song', 'songs'],             kind: 'together', art: '🎵',  clue: 'You sing it out loud, all the way home.',          tint: '#c9a0ff', firstLetter: 'S'  },
  garden: { id: 'garden', word: 'garden', accept: ['garden', 'gardens'],         kind: 'together', art: '🌻',  clue: 'Out the back, with flowers and mud in it.',        tint: '#b7e08a', firstLetter: 'G'  },
  picnic: { id: 'picnic', word: 'picnic', accept: ['picnic', 'picnics'],         kind: 'together', art: '🧺',  clue: 'Food on a rug, out on the grass.',                 tint: '#e6d18a', firstLetter: 'P'  },
  lunch:  { id: 'lunch',  word: 'lunch',  accept: ['lunch', 'lunches'],          kind: 'together', art: '🥪',  clue: 'You eat it in the middle of the day.',             tint: '#ffc08a', firstLetter: 'L'  },
  story:  { id: 'story',  word: 'story',  accept: ['story', 'stories'],          kind: 'together', art: '📖',  clue: 'Somebody reads it to you when the day is done.',   tint: '#ff8fd0', firstLetter: 'St' },
};

/**
 * Words that must never become targets in this game, whatever anybody adds
 * later. Checked by validateContent(); see the header.
 */
export const NEVER_A_TARGET = Object.freeze([
  'youtube', 'you tube', 'phone', 'mobile', 'tablet', 'ipad', 'screen',
  'video', 'app', 'channel', 'telly', 'tv',
]);

export const NEARBY_IDS = Object.keys(NEARBY);

/** @param {string} id @returns {Nearby|null} */
export function getNearby(id) {
  return NEARBY[id] || null;
}

/**
 * The phrase Pip models and the card displays.
 *
 * One word, always. Phase 1 has an MLU of 1.0 and there is nothing to combine.
 *
 * @param {{objectId:string}} target
 * @returns {string}
 */
export function canonicalPhrase(target) {
  const one = getNearby(target && target.objectId);
  return one ? one.word : '';
}

/**
 * Every string that counts as having produced this target.
 *
 * The engine's judgement.targetProduced() takes ONE target string, so a game
 * with homophone-tolerant targets calls it once per variant. That loop is
 * ../../shared/phrase.js; this is its input.
 *
 * @param {{objectId:string}} target
 * @returns {string[]} most canonical first
 */
export function acceptedPhrases(target) {
  const one = getNearby(target && target.objectId);
  return one ? one.accept.slice() : [];
}

export default { NEARBY, NEARBY_IDS, NEVER_A_TARGET, getNearby, canonicalPhrase, acceptedPhrases };
